import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Paper,
  Chip
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Help as HelpIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material'
import SEO from '@components/SEO'
import {
  CANCEL_SUBSCRIPTION_ANSWER,
  CIGAR_AGING_ANSWER,
  DATA_BACKUP_ANSWER,
  DATA_EXPORT_ANSWER,
  IMPORT_COLLECTION_ANSWER,
  MOBILE_APP_ANSWER,
  OFFLINE_ANSWER,
  SHARE_COLLECTION_ANSWER,
  PRICING_PLANS_ANSWER,
  COLLECTION_VALUE_ANSWER,
  REFUNDS_ANSWER,
  PAYMENT_METHODS_ANSWER,
  PASSWORD_SECURITY_ANSWER,
  PHOTO_UPLOAD_ANSWER,
  ADD_CIGAR_ANSWER,
  DATA_SECURITY_ANSWER,
  GDPR_PRIVACY_ANSWER,
} from '@/content/productFaqCopy'
import { useBetaMode } from '@hooks/useBetaMode'

const FAQPage: React.FC = () => {
  const navigate = useNavigate()
  const { beta } = useBetaMode()
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false)

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : false)
  }

  const faqCategories = [
    {
      title: 'Getting Started',
      icon: '🚀',
      faqs: [
        {
          question: 'How do I create my first humidor?',
          answer: 'After signing up, go to the Collection page and click "Add Humidor". Enter the name, capacity, and location of your humidor. You can add multiple humidors to organize your collection by location or type.'
        },
        {
          question: 'Can I import my existing cigar collection?',
          answer: IMPORT_COLLECTION_ANSWER
        },
        {
          question: 'Is there a mobile app?',
          answer: MOBILE_APP_ANSWER
        },
        {
          question: 'How much does CigarTrack cost?',
          answer: PRICING_PLANS_ANSWER
        }
      ]
    },
    {
      title: 'Features & Usage',
      icon: '⚙️',
      faqs: [
        {
          question: 'How do I add cigars to my collection?',
          answer: ADD_CIGAR_ANSWER
        },
        {
          question: 'Can I track cigar aging?',
          answer: CIGAR_AGING_ANSWER
        },
        {
          question: 'How does the smoking journal work?',
          answer: 'After smoking a cigar, open Smoking Journal and record a session. Rate flavor, construction, burn, and value; add text notes and optional image URLs. Sessions link to cigars in your collection.'
        },
        {
          question: 'Can I share my collection with others?',
          answer: SHARE_COLLECTION_ANSWER
        },
        {
          question: 'How do I track collection value?',
          answer: COLLECTION_VALUE_ANSWER
        }
      ]
    },
    {
      title: 'Account & Billing',
      icon: '💳',
      faqs: [
        {
          question: 'How do I upgrade my plan?',
          answer: 'Paid plans are not available for self-serve checkout yet. Join the waitlist on the Pricing page, or contact support via the Contact page or Report Issue (/app/feedback) for billing questions.'
        },
        {
          question: 'Can I cancel my subscription anytime?',
          answer: CANCEL_SUBSCRIPTION_ANSWER
        },
        {
          question: 'Do you offer refunds?',
          answer: REFUNDS_ANSWER
        },
        {
          question: 'What payment methods do you accept?',
          answer: PAYMENT_METHODS_ANSWER
        },
        {
          question: 'How do I change my password?',
          answer: PASSWORD_SECURITY_ANSWER
        }
      ]
    },
    {
      title: 'Technical Support',
      icon: '🔧',
      faqs: [
        {
          question: 'The app is running slowly. What should I do?',
          answer: 'Try refreshing the page or clearing your browser cache. If the issue persists, check your internet connection or try using a different browser. Contact support if problems continue.'
        },
        {
          question: 'I can\'t upload photos. What\'s wrong?',
          answer: PHOTO_UPLOAD_ANSWER
        },
        {
          question: 'How do I export my data?',
          answer: DATA_EXPORT_ANSWER
        },
        {
          question: 'Is my data backed up?',
          answer: DATA_BACKUP_ANSWER
        },
        {
          question: 'Can I use CigarTrack offline?',
          answer: OFFLINE_ANSWER
        }
      ]
    },
    {
      title: 'Privacy & Security',
      icon: '🔒',
      faqs: [
        {
          question: 'How secure is my data?',
          answer: DATA_SECURITY_ANSWER
        },
        {
          question: 'Can I delete my account and data?',
          answer: 'Yes, you can delete your account and all associated data from Settings > Account > Delete Account. This action is irreversible, so make sure to export your data first if needed.'
        },
        {
          question: 'Do you sell my data to third parties?',
          answer: 'Never. We don\'t sell, rent, or share your personal data with third parties. Your collection information is private and only accessible to you.'
        },
        {
          question: 'How do you handle GDPR compliance?',
          answer: GDPR_PRIVACY_ANSWER
        }
      ]
    }
  ]

  const filteredCategories = faqCategories.map(category => ({
    ...category,
    faqs: category.faqs.filter(faq => {
      if (beta && category.title === 'Account & Billing' && !faq.question.toLowerCase().includes('password')) {
        return false
      }
      return (
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })
  })).filter(category => category.faqs.length > 0)

  const popularQuestions = [
    'How do I add cigars to my collection?',
    'Is there a mobile app?',
    ...(beta ? [] : ['How much does CigarTrack cost?']),
    'Can I import my existing collection?',
    'How secure is my data?',
  ]

  return (
    <Box>
      <SEO
        title="FAQ - CigarTrack"
        description="Frequently asked questions about CigarTrack. Find answers about managing your cigar collection, humidor tracking, subscriptions, and more."
        keywords="CigarTrack FAQ, cigar app help, humidor management questions"
        url="/faq"
      />
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #DAA520 100%)',
          color: 'white',
          py: { xs: 6, md: 8 }
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                lineHeight: 1.2
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9,
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Find answers to common questions about CigarTrack. 
              Can&apos;t find what you&apos;re looking for? Contact our support team.
            </Typography>
            
            {/* Search Box */}
            <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
              <TextField
                fullWidth
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.3)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255,255,255,0.5)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'white'
                    }
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255,255,255,0.7)'
                  }
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Popular Questions */}
      <Box sx={{ py: 6, backgroundColor: '#F5F5DC' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ color: '#8B4513', fontWeight: 600, mb: 4 }}
          >
            Popular Questions
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            {popularQuestions.map((question, index) => (
              <Chip
                key={index}
                label={question}
                onClick={() => setSearchTerm(question.split(' ').slice(0, 3).join(' '))}
                sx={{
                  backgroundColor: 'white',
                  color: '#8B4513',
                  border: '1px solid #8B4513',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#8B4513',
                    color: 'white'
                  }
                }}
              />
            ))}
          </Box>
        </Container>
      </Box>

      {/* FAQ Categories */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          {filteredCategories.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <HelpIcon sx={{ fontSize: 60, color: '#8B4513', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No FAQs found for &quot;{searchTerm}&quot;
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Try searching with different keywords or contact our support team for help.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/contact')}
                sx={{
                  backgroundColor: '#8B4513',
                  '&:hover': { backgroundColor: '#7A3D0F' }
                }}
              >
                Contact Support
              </Button>
            </Paper>
          ) : (
            filteredCategories.map((category, categoryIndex) => (
              <Box key={categoryIndex} sx={{ mb: 6 }}>
                <Typography
                  variant="h4"
                  component="h2"
                  gutterBottom
                  sx={{ color: '#8B4513', fontWeight: 600, mb: 3 }}
                >
                  {category.icon} {category.title}
                </Typography>
                {category.faqs.map((faq, faqIndex) => (
                  <Accordion
                    key={faqIndex}
                    expanded={expandedAccordion === `${categoryIndex}-${faqIndex}`}
                    onChange={handleAccordionChange(`${categoryIndex}-${faqIndex}`)}
                    sx={{
                      mb: 1,
                      '&:before': {
                        display: 'none'
                      },
                      '&.Mui-expanded': {
                        margin: '0 0 8px 0'
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: '#f8f9fa',
                        '&:hover': {
                          backgroundColor: '#e9ecef'
                        },
                        '&.Mui-expanded': {
                          backgroundColor: '#e9ecef'
                        }
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ backgroundColor: 'white' }}>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ))
          )}
        </Container>
      </Box>

      {/* Still Need Help Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #DAA520 100%)',
          color: 'white',
          py: 8
        }}
      >
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              Still Need Help?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Can&apos;t find the answer you&apos;re looking for? Our support team is here to help!
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/contact')}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  backgroundColor: 'white',
                  color: '#8B4513',
                  px: 6,
                  py: 2,
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                Contact Support
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/register')}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 4,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Try CigarTrack Free
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  )
}

export default FAQPage

